'use client';

import { createContext, useContext, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { ContactModal } from '@/components/modals/lazy';

type ModalType = 'contact';

type ModalContextValue = {
  openModal: (type: ModalType, source?: string) => void;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextValue | null>(null);

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
}

export function ModalProvider({ children }: PropsWithChildren) {
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [source, setSource] = useState<string | undefined>();
  const [everOpened, setEverOpened] = useState(false);

  function openModal(type: ModalType, src?: string) {
    setEverOpened(true);
    setActiveModal(type);
    setSource(src);
  }

  function closeModal() {
    setActiveModal(null);
    setSource(undefined);
  }

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {everOpened && (
        <ContactModal open={activeModal === 'contact'} onClose={closeModal} source={source} />
      )}
    </ModalContext.Provider>
  );
}
